import { Grid, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  InfoWrapper,
  RHFDatePicker,
  RHFMultiSelect,
  RHFTextField,
} from 'components';
import {
  FenomenoIspezioneSpecialeIdraulico,
  IndicatoriIspezioniSpecialiErosione,
  IndicatoriIspezioniSpecialiSormonto,
} from 'constants/inspections';
import { useInspectionPath } from 'hooks';
import { INFO } from 'pages/Inspections/config/info';
import { useInspectionSelector } from 'stores';

const Accordion1GeneralInfoL3 = () => {
  const { watch } = useFormContext();
  // e-2026-06-t21 — stesso pattern di disabilitazione campi di L0/L1
  // (disabled={inspectionDisabled || isViewingInspection}): il form L3
  // (useInspectionL3.js) è indipendente dal form condiviso L0/L1 e finora
  // non rispettava né i permessi né la rotta "view" (bottone
  // "Approfondisci" di TabL3.jsx naviga sempre in modalità view).
  const { inspectionDisabled } = useInspectionSelector();
  const { isViewingInspection } = useInspectionPath();
  const readOnly = inspectionDisabled || isViewingInspection;

  // Issue #1021, punto 18 — 97c/97f (indicatori) vanno mostrati solo in base
  // a 97b (ispezioniSpecialiFenomeni, il fenomeno per cui l'ultima ispezione
  // L1 ha rilevato indicatori), NON in base ad analisiFenomenoAnalizzato
  // (ID577, la scelta dell'utente in QUESTA attività L3): sono due concetti
  // distinti, il primo eredita dati storici, il secondo è l'analisi corrente.
  const watchIspezioniSpecialiFenomeni = watch('ispezioniSpecialiFenomeni');
  const showSormonto = watchIspezioniSpecialiFenomeni?.includes(
    FenomenoIspezioneSpecialeIdraulico.Sormonto
  );
  const showErosione = watchIspezioniSpecialiFenomeni?.includes(
    FenomenoIspezioneSpecialeIdraulico.Erosione
  );

  // Issue #1021, riapertura 2026-09-23 — il prefill "data odierna se
  // l'attività non ne ha ancora una" viveva qui come useEffect separato
  // (setValue + reset(getValues()) al mount), soggetto a una race con la GET
  // reale dei dati L3 (handleDetail in useInspectionL3.js): gli accordion
  // montano SUBITO col form vuoto (L3Idraulica.jsx non ha alcun gate sul
  // caricamento), quindi questo reset() catturava un baseline "pulito" PRIMA
  // che i dati veri arrivassero — la reset() successiva con i dati reali non
  // includeva dataAttivita (mai tra i campi condivisi prefillati dal BE) e
  // la riportava a `undefined`, ma l'effect (deps `[]`, solo mount) non
  // poteva rilanciarsi per rimediare: risultato, banner "modifiche non
  // salvate" (Accordion5CdaL3.jsx) visibile ad attività appena aperta, senza
  // alcun input utente. Spostato dentro mappingDataInL3
  // (utils/mappingInL3.js), che fa parte dello stesso reset che stabilisce
  // il baseline reale — nessuna race possibile, il campo arriva già
  // precompilato quando serve.

  return (
    <>
      <Grid container spacing={2} mt={2}>
        <InfoWrapper
          size={3}
          infoMessage={INFO.analisiFenomenoAnalizzato.infoMessage}
        >
          <RHFMultiSelect
            name="analisiFenomenoAnalizzato"
            label={'Analisi L3 - Fenomeno analizzato*'}
            defaultValue={[]}
            disabled={readOnly}
            selectOptions={[
              {
                label: 'Sormonto',
                value: FenomenoIspezioneSpecialeIdraulico.Sormonto,
              },
              {
                label: 'Erosione',
                value: FenomenoIspezioneSpecialeIdraulico.Erosione,
              },
            ]}
          />
        </InfoWrapper>

        {/* Issue #1021, riapertura 2026-09-21, punto 17 — non segnalati dal
        cliente ma stesso identico problema: "rilevatore"/"dataAttivita" non
        wrappati in InfoWrapper come "analisiFenomenoAnalizzato" sulla stessa
        riga, restavano 5px più in alto. Nessun testo info esiste per questi
        due campi. */}
        <InfoWrapper size={3}>
          <RHFTextField
            name="rilevatore"
            label={'Rilevatore'}
            inputProps={{ maxLength: 50 }}
            defaultValue=""
            disabled={readOnly}
          />
        </InfoWrapper>
        <InfoWrapper size={3}>
          <RHFDatePicker
            label="Data di attività"
            name="dataAttivita"
            disabled={readOnly}
          />
        </InfoWrapper>
      </Grid>
      {/* Issue #1021, punto 17 — ID97b/97c/97f raggruppati sotto un
      titoletto, ereditati dall'ultima ispezione L1 (vedi commento sopra
      showSormonto/showErosione). Niente GridWrap/TitleSection (margini
      negativi tarati per un altro layout, qui rendevano il titolo
      invisibile o sovrapposto ai campi): margini positivi semplici. */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} mb={1}>
          <Typography variant="h3BoldBlue">
            DATI DA ULTIMA ISPEZIONE L1
          </Typography>
        </Grid>
        {/* Issue #1021, riapertura 2026-09-04, punto 17 — non wrappato in
        InfoWrapper come i due campi "Indicatori ispezioni speciali
        Sormonto/Erosione" sulla stessa riga, restava più in alto (stesso
        pattern/causa del punto 22 in Accordion2HydraulicDataL3.jsx). Nessun
        testo info esiste per questo campo: InfoWrapper senza messaggi non
        renderizza il badge, applica solo lo spacer di allineamento. */}
        <InfoWrapper size={3}>
          <RHFMultiSelect
            name="ispezioniSpecialiFenomeni"
            label={'Ispezioni speciali: fenomeni'}
            defaultValue={[]}
            disabled
            selectOptions={[
              {
                label: 'Sormonto',
                value: FenomenoIspezioneSpecialeIdraulico.Sormonto,
              },
              {
                label: 'Erosione',
                value: FenomenoIspezioneSpecialeIdraulico.Erosione,
              },
            ]}
          />
        </InfoWrapper>
        {showSormonto && (
          <InfoWrapper
            size={3}
            infoDrawer={true}
            drawerTitle={INFO.indicatoriIspezioniSpecialiSormonto.drawerTitle}
            drawerText={INFO.indicatoriIspezioniSpecialiSormonto.drawerText}
          >
            <RHFMultiSelect
              name="indicatoriIspezioniSpecialiSormonto"
              label={'Indicatori ispezioni speciali Sormonto'}
              defaultValue={[]}
              disabled
              selectOptions={[
                {
                  label: 'Materiale flottante su struttura',
                  value:
                    IndicatoriIspezioniSpecialiSormonto.MaterialeFlottanteSuStruttura,
                },
                {
                  label: 'Passaggio piena su struttura',
                  value:
                    IndicatoriIspezioniSpecialiSormonto.PassaggioPienaSuStruttura,
                },
                {
                  label: 'Tracce di alluvione su terreni adiacenti',
                  value:
                    IndicatoriIspezioniSpecialiSormonto.TracceDiAlluvioneSuTerreniAdiacenti,
                },
                {
                  label: 'Urti su struttura',
                  value: IndicatoriIspezioniSpecialiSormonto.UrtiSuStruttura,
                },
                {
                  label: 'Altro (sormonto)',
                  value: IndicatoriIspezioniSpecialiSormonto.Altro,
                },
              ]}
            />
          </InfoWrapper>
        )}
        {showErosione && (
          <InfoWrapper
            size={3}
            infoDrawer={true}
            drawerTitle={INFO.indicatoriIspezioniSpecialiErosione.drawerTitle}
            drawerText={INFO.indicatoriIspezioniSpecialiErosione.drawerText}
          >
            <RHFMultiSelect
              name="indicatoriIspezioniSpecialiErosione"
              label={'Indicatori ispezioni speciali Erosione'}
              defaultValue={[]}
              disabled
              selectOptions={[
                {
                  label: 'Scalzamento su pile e/o spalle',
                  value:
                    IndicatoriIspezioniSpecialiErosione.ScalzamentoSuPileESpalle,
                },
                {
                  label: 'Pile e/o spalle sommerse',
                  value:
                    IndicatoriIspezioniSpecialiErosione.PileESpalleSommerse,
                },
                {
                  label: 'Danneggiamento opere protezione al piede',
                  value:
                    IndicatoriIspezioniSpecialiErosione.DanneggiamentoOpereProtezioneAlPiede,
                },
                {
                  label: 'Danneggiamento briglia a valle',
                  value:
                    IndicatoriIspezioniSpecialiErosione.DanneggiamentoBrigliaAValle,
                },
                {
                  label: 'Erosione spondale in prossimità',
                  value:
                    IndicatoriIspezioniSpecialiErosione.ErosioneSpondaleInProssimita,
                },
                {
                  label: 'Accentuata divagazione planimetrica',
                  value:
                    IndicatoriIspezioniSpecialiErosione.AccentuataDivagazionePlanimetrica,
                },
                {
                  label: 'Danneggiamento opere protezione laterali',
                  value:
                    IndicatoriIspezioniSpecialiErosione.DanneggiamentoOpereProtezioneLaterali,
                },
                {
                  label: 'Altro (erosione)',
                  value: IndicatoriIspezioniSpecialiErosione.Altro,
                },
              ]}
            />
          </InfoWrapper>
        )}
      </Grid>
    </>
  );
};

export { Accordion1GeneralInfoL3 };
